import { apiService } from '@services/apiService';
import { types, flow } from 'mobx-state-tree';
import { getToken } from '@utils/tokenUtils';
import i18n from '../../i18n';
import { userNavItems } from '@components/Core/Navbar/constatnts';
import { businessRoutes, commonRoutes } from '@/routes/routes';
import { withAlertStore } from '../alert/withAlertStore';
import { withMainStore } from '../main/withMainStore';
import { IUserChangeProfile, IUserProfile } from './types';

const { model, frozen, maybeNull } = types;

export const ProfileStore = model('ProfileStore', {
  user: maybeNull(frozen<IUserProfile>()),
})
  .extend(withAlertStore)
  .extend(withMainStore)
  .views((self) => ({
    get redirectUrl() {
      switch (self.user?.role) {
        case 'customer':
          return '/';
        case 'business':
          return '/brands';
        default:
          return '/';
      }
    },
    get userRoutes() {
      switch (self.user?.role) {
        case 'customer':
          return commonRoutes;
        case 'business':
          return [...commonRoutes, ...businessRoutes];
        default:
          return [...commonRoutes, ...businessRoutes].map((item) =>
            item.isPrivate ? { ...item, component: null } : item
          );
      }
    },
    get usersNavBar() {
      switch (self.user?.role) {
        case 'customer':
          return [];
        case 'business':
          return userNavItems;
        default:
          return [];
      }
    },
  }))
  .actions((self) => ({
    setUser: flow(function* (user: IUserProfile | null) {
      const { setInitSelectedCountryCode, setGlobalLoading, setTheme } = self.mainStore;
      if (user) {
        if (user.usersCountryCodes.length) {
          setInitSelectedCountryCode({ id: user.id, countryCodeData: user.usersCountryCodes[0] });
        }
        setTheme(user.isDarkTheme ? 'dark' : 'light');
        if (user.lang && user.lang !== self.user?.lang) {
          yield i18n.changeLanguage(user.lang);
          apiService.changeLanguage(user.lang);
        }
      }
      self.user = user;
      setGlobalLoading(false);
    }),
  }))
  .actions((self) => ({
    afterCreate() {
      const token = getToken();
      if (token) {
        self.mainStore.setGlobalLoading(true);
        this.getProfile();
      }
    },
    getProfile: flow(function* () {
      yield apiService
        .get('get-profile')
        .then(({ data: { data } }) => self.setUser(data))
        .catch(self.mainStore.errorHandling);
    }),
    changeProfile: flow(function* (data: IUserChangeProfile) {
      yield apiService
        .patch('update-profile', data)
        .then(({ data: { data } }) => self.setUser(data))
        .catch(self.mainStore.errorHandling);
    }),
    changeEmail: flow(function* (email: string) {
      yield apiService
        .post('profile/change-email', { email })
        // .then(({ data: { data } }) => self.setUser(data))
        .catch(self.mainStore.errorHandling);
    }),
    veriifyExistingEmail: flow(function* (code: string) {
      yield apiService
        .post('profile/verify-existing-user-email', { code })
        .catch(self.mainStore.errorHandling);
    }),
    verifyNewEmail: flow(function* (data: { email: string; code: string }) {
      return yield apiService
        .post('profile/verify-new-user-email', data)
        .then(({ data: { data } }) => data)
        .catch(self.mainStore.errorHandling);
    }),
    deleteProfile: flow(function* () {
      yield apiService.delete(`profile/delete`).catch(self.mainStore.errorHandling);
    }),
  }));
